/**
 * Image loading utilities (client-focused)
 */

const pendingPromises = new Map<string, Promise<HTMLImageElement>>();
const imageCache = new Map<string, HTMLImageElement>();

/**
 * Load a single image and resolve once it's fully loaded.
 * - Dedupes concurrent requests for the same URL
 * - Caches the loaded HTMLImageElement for subsequent calls
 * Returns the HTMLImageElement on success.
 */
export const loadImage = (src: string): Promise<HTMLImageElement> => {
  if (!import.meta.client) {
    // SSR: Skip image loading
    return Promise.reject(
      new Error(
        "loadImage is not supported in SSR (server-side rendering) context",
      ),
    );
  }

  const cached = imageCache.get(src);
  if (cached) return Promise.resolve(cached);

  // Return existing pending promise if present
  const existing = pendingPromises.get(src);
  if (existing) return existing;

  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      imageCache.set(src, img);
      pendingPromises.delete(src);
      resolve(img);
    };
    img.onerror = () => {
      pendingPromises.delete(src);
      reject(new Error(`Failed to load image: ${src}`));
    };
    img.src = src;
  });

  pendingPromises.set(src, promise);

  return promise;
};
